/**
 * API Service Layer
 *
 * Centralized API client for communicating with AWS Lambda functions via API Gateway.
 * Uses Cognito JWT token for authentication.
 */

import { fetchAuthSession, signOut } from 'aws-amplify/auth';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

// Buffer in seconds — refresh token if it expires within this window
const TOKEN_REFRESH_BUFFER_S = 60;

/**
 * Gets a valid auth token, proactively refreshing if near expiry.
 * Signs out and throws if no session is available.
 */
async function getValidToken() {
  let session = await fetchAuthSession();
  const idToken = session.tokens?.idToken;

  if (!idToken) {
    await signOut().catch(() => {});
    throw new Error('No authentication token available');
  }

  // Proactive refresh: if token expires within the buffer window, force-refresh
  const exp = idToken.payload?.exp;
  if (exp && exp - Math.floor(Date.now() / 1000) < TOKEN_REFRESH_BUFFER_S) {
    session = await fetchAuthSession({ forceRefresh: true });
    const refreshedToken = session.tokens?.idToken;
    if (!refreshedToken) {
      await signOut().catch(() => {});
      throw new Error('No authentication token available');
    }
    return refreshedToken.toString();
  }

  return idToken.toString();
}

/**
 * Executes a fetch request, handling 401 responses with auto-signout.
 */
async function makeRequest(url, requestOptions) {
  const response = await fetch(url, requestOptions);

  if (response.status === 401) {
    await signOut().catch(() => {});
    throw new Error('Session expired. Please sign in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const message = errorData?.error || errorData?.message || `API Error: ${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return response.json();
}

/**
 * Makes an authenticated API request with JWT token
 */
async function apiRequest(path, options = {}) {
  const { method = 'GET', body } = options;

  try {
    const token = await getValidToken();

    const url = `${API_ENDPOINT}${path}`;
    const requestOptions = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
    };

    if (body) {
      requestOptions.body = JSON.stringify(body);
    }

    return await makeRequest(url, requestOptions);
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * API methods organized by domain
 */
export const api = {
  // ============================================
  // PATIENTS
  // ============================================

  /**
   * Fetch all patients for the current clinic
   */
  getPatients: () => apiRequest('/patients'),

  /**
   * Get patient status counts for nav badges (no PHI)
   */
  getNavCounts: () => apiRequest('/patients/nav-counts'),

  /**
   * Get a single patient by ID
   */
  getPatient: (patientId) => apiRequest(`/patients/${patientId}`),

  /**
   * Update a patient
   */
  updatePatient: (patientId, updates) =>
    apiRequest(`/patients/${patientId}`, {
      method: 'PUT',
      body: updates,
    }),

  /**
   * Bulk create patients (from CSV upload)
   * Includes an idempotency key to prevent duplicate creation on retries.
   * @param {Array} patients - Array of patient objects
   * @param {string} [fileName] - Original CSV file name for import tracking
   */
  createPatients: (patients, fileName) =>
    apiRequest('/patients/bulk', {
      method: 'POST',
      body: {
        patients,
        file_name: fileName || null,
        idempotency_key: `upload_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
      },
    }),

  /**
   * Delete a patient
   */
  deletePatient: (patientId) =>
    apiRequest(`/patients/${patientId}`, {
      method: 'DELETE',
    }),

  // ============================================
  // CAMPAIGNS
  // ============================================

  /**
   * Fetch all campaigns for the current clinic
   */
  getCampaigns: () => apiRequest('/campaigns'),

  /**
   * Get a single campaign by ID
   */
  getCampaign: (campaignId) => apiRequest(`/campaigns/${campaignId}`),

  /**
   * Create a new campaign
   */
  createCampaign: (campaignData) =>
    apiRequest('/campaigns', {
      method: 'POST',
      body: campaignData,
    }),

  /**
   * Update a campaign
   */
  updateCampaign: (campaignId, updates) =>
    apiRequest(`/campaigns/${campaignId}`, {
      method: 'PUT',
      body: updates,
    }),

  /**
   * Get patients associated with a campaign
   */
  getCampaignPatients: (campaignId) =>
    apiRequest(`/campaigns/${campaignId}/patients?enrich=true`),

  // ============================================
  // MESSAGING
  // ============================================

  /**
   * Send an SMS message
   */
  sendSms: (data) =>
    apiRequest('/send-sms', {
      method: 'POST',
      body: data,
    }),

  /**
   * Send an email
   */
  sendEmail: (data) =>
    apiRequest('/send-email', {
      method: 'POST',
      body: data,
    }),

  /**
   * Send a secure message (creates token + sends email with link)
   */
  sendSecureMessage: (data) =>
    apiRequest('/send-secure-message', {
      method: 'POST',
      body: data,
    }),

  /**
   * Create a secure message token (without sending)
   */
  createSecureMessage: (data) =>
    apiRequest('/create-secure-message', {
      method: 'POST',
      body: data,
    }),

  /**
   * Verify secure message token with DOB
   * Note: This endpoint does not require authentication
   */
  verifySecureMessage: async ({ token, dob }) => {
    const response = await fetch(`${API_ENDPOINT}/verify-secure-message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, dob }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || 'Failed to verify message');
    }

    return response.json();
  },

  // ============================================
  // AI / ANALYSIS
  // ============================================

  /**
   * Generate AI message for patient outreach
   */
  generateMessage: (data) =>
    apiRequest('/generate-message', {
      method: 'POST',
      body: data,
    }),

  /**
   * Analyze patient risk using AI
   */
  analyzeRisk: (data) =>
    apiRequest('/analyze-risk', {
      method: 'POST',
      body: data,
    }),

  // ============================================
  // CLINIC RULES
  // ============================================

  /**
   * Get all rules for a clinic
   */
  getClinicRules: () => apiRequest('/clinic-rules'),

  /**
   * Update a clinic rule
   */
  updateClinicRule: (ruleId, updates) =>
    apiRequest(`/clinic-rules/${ruleId}`, {
      method: 'PUT',
      body: updates,
    }),

  // ============================================
  // IMPORT RUNS
  // ============================================

  /**
   * Get all import runs for the current clinic
   */
  getImportRuns: () => apiRequest('/import-runs'),

  /**
   * Get a single import run by ID
   */
  getImportRun: (runId) => apiRequest(`/import-runs/${runId}`),

  // ============================================
  // OUTREACH LOGS
  // ============================================

  /**
   * Get outreach logs for a patient
   */
  getPatientOutreachLogs: (patientId) =>
    apiRequest(`/patients/${patientId}/outreach-logs`),

  /**
   * Create an outreach log entry
   */
  createOutreachLog: (data) =>
    apiRequest('/outreach-logs', {
      method: 'POST',
      body: data,
    }),

  // ============================================
  // ENGAGEMENT / SECURE MESSAGE ACCESS
  // ============================================

  /**
   * Get engagement logs (secure message access)
   */
  getEngagementLogs: () => apiRequest('/engagement-logs'),

  // ============================================
  // CLINIC / PROFILE
  // ============================================

  /**
   * Get current user's profile with clinic info
   */
  getCurrentProfile: () => apiRequest('/profile'),

  /**
   * Get clinic information
   */
  getClinic: () => apiRequest('/clinic'),

  /**
   * Update clinic (e.g., mark onboarding complete)
   */
  updateClinic: (updates) =>
    apiRequest('/clinic', {
      method: 'PUT',
      body: updates,
    }),

  /**
   * Update current user's profile
   */
  updateProfile: (updates) =>
    apiRequest('/profile', {
      method: 'PUT',
      body: updates,
    }),

  /**
   * Sync email from verified Cognito token to DynamoDB profile
   */
  syncProfileEmail: () =>
    apiRequest('/profile/sync-email', {
      method: 'POST',
    }),

  /**
   * Get team members for the current clinic
   */
  getTeamMembers: () => apiRequest('/team'),

  /**
   * Invite a new team member
   */
  inviteTeamMember: ({ email, role }) =>
    apiRequest('/team/invite', {
      method: 'POST',
      body: { email, role },
    }),

  /**
   * Remove a team member
   */
  removeTeamMember: (userId) =>
    apiRequest(`/team/${userId}`, {
      method: 'DELETE',
    }),

  /**
   * Get report stats (outreach count) for a month/year
   */
  getReportStats: (month, year) =>
    apiRequest(`/report-stats?month=${month}&year=${year}`),

  // ============================================
  // SUPER-ADMIN (platform-level)
  // ============================================

  getSuperAdminClinics: (params = {}) => {
    const query = params.cursor ? `?cursor=${encodeURIComponent(params.cursor)}` : '';
    return apiRequest(`/super-admin/clinics${query}`);
  },

  getSuperAdminClinicDetail: (clinicId) =>
    apiRequest(`/super-admin/clinics/${clinicId}`),

  createClinicWithAdmin: (data) =>
    apiRequest('/super-admin/clinics', {
      method: 'POST',
      body: data,
    }),

  getSuperAdminStats: () => apiRequest('/super-admin/stats'),

  resendClinicInvite: (clinicId, email) =>
    apiRequest(`/super-admin/clinics/${clinicId}/resend-invite`, {
      method: 'POST',
      body: { email },
    }),

  removeSuperAdminClinicMember: (clinicId, userId) =>
    apiRequest(`/super-admin/clinics/${clinicId}/members/${userId}`, {
      method: 'DELETE',
    }),

  resetClinic: (clinicId) =>
    apiRequest(`/super-admin/clinics/${clinicId}/reset`, {
      method: 'POST',
    }),

  // ============================================
  // REPORTS
  // ============================================

  /**
   * Send summary report
   */
  sendSummaryReport: (data) =>
    apiRequest('/send-summary-report', {
      method: 'POST',
      body: data,
    }),
};

export default api;
